import css from '@styled-system/css'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  color,
  space,
  border,
  compose,
  variant,
  typography,
  flexbox,
  system,
  layout,
} from 'styled-system'

const textTransform = system({
  textTransform: true,
})

const styleProps = compose(
  color,
  space,
  border,
  typography,
  flexbox,
  textTransform,
  layout,
)

const variants = ({ disabled = false, $shouldShowShadow, theme } = {}) => ({
  primary: {
    fontWeight: 'medium',
    color: disabled ? 'btn.primary.textDisabled' : 'btn.primary.text',
    bg: 'btn.primary.bg',
    ...(!disabled && {
      boxShadow: $shouldShowShadow
        ? `${theme.colors.shadow.primary} 20px 20px 70px`
        : 'none',
      '@media (pointer: fine)': {
        '&:hover': {
          bg: 'btn.primary.bgHover',
          color: 'btn.primary.bg',
          borderColor: 'btn.primary.bg',
          path: {
            color: 'btn.primary.bg',
            stroke: 'btn.primary.bg',
          },
        },
      },
    }),
  },
  secondary: {
    color: 'btn.secondary.text',
    bg: 'btn.secondary.bg',
    fontWeight: 'medium',
    borderColor: 'btn.secondary.border',
    ...(!disabled && {
      boxShadow: $shouldShowShadow ? '5px 4px 15px 0px #515BAF26' : 'none',
      '&:hover': {
        borderColor: 'btn.secondary.borderHover',
      },
    }),
  },
  transparent: {
    bg: 'transparent',
  },
  success: {
    fontWeight: 'medium',
    color: 'btn.success.text',
    bg: 'btn.success.bg',
    opacity: '1',
    '&:hover': {
      opacity: '1',
    },
  },
  danger: {
    color: 'text.red',
    bg: 'grayMedium',
    fontWeight: 'medium',
    '&:hover': {
      borderColor: 'text.red',
    },
    ...(!disabled && {
      boxShadow: $shouldShowShadow ? '5px 4px 15px 0px #515BAF26' : 'none',
    }),
  },
  inCircle: {
    bg: 'btn.inCircle.bg',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow: '4px 4px 20px rgba(0, 23, 233, 0.07)',
    border: '2px solid',
    borderColor: 'btn.inCircle.border',
  },
  iconButton: {
    background: 'btn.iconButton.bg',
    border: 'btn.iconButton.border',
  },
  dashedBlue: {
    borderWidth: '1.5px',
    borderStyle: 'dashed',
    borderColor: 'btn.dashedBlue.border',
    borderRadius: '1.125rem',
    bg: 'btn.dashedBlue.bg',
  },
  mobileAppButton: {
    backgroundColor: 'bg.documentSectionGray',
    borderRadius: '1rem',
    flexDirection: 'column',
    p: 4,
    gap: 4,
    height: '100%',
    width: '100%',
  },
  mobileSelectButton: {
    backgroundColor: 'bg.dividerLine',
    borderRadius: '1rem',
    p: 4,
    color: 'btn.secondary.text',
  },
  ghost: {
    bg: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      bg: 'transparent',
      textDecoration: 'underline',
    },
    px: 0,
  },
})

const size = {
  xs: {
    py: '0.5rem',
    px: '1.25rem',
    minWidth: '3.125rem',
    height: '1.5rem',
    fontSize: 'xs',
  },
  sm: {
    py: '0.75rem',
    px: '1rem',
    minWidth: '5.375rem',
    height: '2.25rem',
    fontSize: 'sm',
  },
  md: {
    px: '1rem',
    minWidth: '5.625rem',
    height: '48px',
    fontSize: 'sm',
  },
  lg: {
    minWidth: '12rem',
    p: '0.75rem 1.75rem',
    fontSize: 'sm',
  },
  xl: {
    minWidth: '13.5rem',
    height: '4.25rem',
    fontSize: 'xl',
  },
  action: {
    px: '3.25rem',
    py: '1.3rem',
    borderRadius: 'lg',
    boxShadow: '2.5rem 3.5rem 5rem rgba(0, 23, 233, 0.25)',
  },
}

export const sizeProps = Object.keys(size)
const sizePropTypes = PropTypes.oneOf(sizeProps)

export const variantProps = Object.keys(variants())
const variantPropTypes = PropTypes.oneOf(variantProps)

export const ButtonStyles = styled.button(
  ({ $fullWidth, disabled }) =>
    css({
      cursor: 'pointer',
      width: $fullWidth ? '100%' : 'auto',
      fontWeight: 'normal',
      borderRadius: '1.875rem',
      boxSizing: 'border-box',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'btn.default.border',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: '.2s',
      fontFamily: 'Lexend, sans-serif',
      whiteSpace: 'nowrap',
      ...(!disabled && {
        '&:hover': {
          opacity: '0.9',
        },
      }),
    }),
  ({ disabled }) =>
    disabled &&
    css({
      color: 'btn.default.textDisabled',
      bg: 'btn.default.bgDisabled',
      fontWeight: 'medium',
      cursor: 'default',
      opacity: '0.4',
    }),
  variant({
    prop: 'size',
    variants: size,
  }),
  ({ disabled, $shouldShowShadow, visible, isToggled }) =>
    variant({
      variants: variants({ disabled, $shouldShowShadow, visible, isToggled }),
    }),
  ({ visible }) =>
    !visible &&
    css({
      display: 'none',
    }),
  styleProps,
)

ButtonStyles.propTypes = {
  size: PropTypes.oneOfType([sizePropTypes, PropTypes.arrayOf(sizePropTypes)]),
  variant: PropTypes.oneOfType([
    variantPropTypes,
    PropTypes.arrayOf(variantPropTypes),
  ]),
}
